import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { Calendar as CalendarIcon } from "lucide-react";

const STATUS_STYLES = {
  scheduled: "bg-primary/10 text-primary",
  completed: "bg-secondary/15 text-secondary",
  cancelled: "bg-destructive/10 text-destructive",
  no_show: "bg-muted text-muted-foreground",
};

export default function DoctorAppointments() {
  const [appts, setAppts] = useState([]);

  const load = async () => {
    const { data } = await api.get("/doctor/appointments");
    setAppts(data);
  };
  useEffect(() => { load(); }, []);

  const update = async (id, status) => {
    try {
      await api.put(`/doctor/appointments/${id}`, { status });
      toast.success("Updated");
      load();
    } catch {
      toast.error("Could not update");
    }
  };

  return (
    <AppShell pageTitle="Appointments" pageDescription="Manage your consultation queue and outcomes.">
      {appts.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center">
          <CalendarIcon size={28} className="mx-auto text-muted-foreground" />
          <div className="mt-3 font-serif text-xl text-primary">No appointments yet</div>
          <p className="text-sm text-muted-foreground mt-1">Patients will show up here as they book.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-white overflow-hidden" data-testid="doctor-appointments-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/60">
              <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Patient</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Reason</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {appts.map((a) => (
                <tr key={a.id} className="border-t border-border" data-testid={`doctor-appointment-row-${a.id}`}>
                  <td className="px-4 py-3 font-medium text-primary">{a.patient_name}</td>
                  <td className="px-4 py-3">{formatDate(a.date)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{a.reason}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 ${STATUS_STYLES[a.status] || STATUS_STYLES.scheduled}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Select value={a.status} onValueChange={(v) => update(a.id, v)}>
                      <SelectTrigger className="h-8 w-40 text-xs" data-testid={`appointment-status-select-${a.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="scheduled">Scheduled</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                        <SelectItem value="no_show">No show</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}
